import { Component, computed, effect, signal, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { DataService } from '@app/common-codes/services/DataService';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { QrcodeService } from '@app/common-codes/services/qrcode.service';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { PointOfService } from '@app/common-codes/models/matwins-tenant/PointOfService.model';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { DependencyInjectionProvider } from '@app/common-codes/services/DependecyInjection.provider';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { AlertModalResult } from '@app/common-codes/components/nested-components/alert-dialog/alert-dialog.component';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';

@Component({
  selector: 'app-ipad-home',
  imports: [TranslatePipe],
  templateUrl: './ipad-home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class IPadHomeComponent {

  baseQrCodeUrl = '';
  showQrCode = false;
  private lastGeneratedQrCodeUrl = '';
  Skin = MatwinsSkins.V1 as IStaticStylesConfig;

  dataService!: DataService<Visit>;
  isAlerting = signal(false);

  showEmployeeQrGenerator = computed(() =>
    !!this.currentTenantService.currentPointOfService()?.dS_Employee_QrCodeGenerator
  );

  showFireAlert = computed(() =>
    !!this.currentTenantService.currentPointOfService()?.dS_FireAlert
  );

  logoUrl = computed(() => {
    const pos = this.currentTenantService.currentPointOfService();
    if (pos?.imageLogo?.rawData_Path) {
      return '/Documents/' + pos.imageLogo.rawData_Path;
    }
    return '/assets/images/Logo-Matwins.png'; // fallback
  });


  constructor(
    private navService: NavigationService,
    private variables: MatwinsVariablesService,
    private qrCodeService: QrcodeService,
    private currentTenantService: CurrentTenantService,
    private translationService: TranslationService,
    private alertModalService: AlertModalService,
    private di: DependencyInjectionProvider

  ) {
    this.baseQrCodeUrl = `${window.location.origin}/qrcode/${this.variables.clientId}/${this.variables.posId}`;
    this.dataService = this.di.getOrCreateDataService(Visit);

    effect(() => {
      if (this.currentTenantService.currentPointOfService()?.dS_ShowQrCode && this.baseQrCodeUrl) {
        this.showQrCode = true;
        this.initQrCode();
      }
    })

  }

  async ngOnInit() {
    const pos = this.currentTenantService.currentPointOfService();
    const lang = await this.resolvePosLanguageKey(pos) || 'en';
    await this.translationService.initializeLanguage(lang);
  }

  // La langue par défaut du site n'est pas toujours chargée avec le POS (ex. getAvailablePos) :
  // on la relit via GET pointofservices/{id}, qui inclut i18nLanguage.
  private async resolvePosLanguageKey(pos: PointOfService | null | undefined): Promise<string | undefined> {
    if (!pos) return undefined;
    if (pos.i18nLanguage?.key) return pos.i18nLanguage.key;
    try {
      const fullPos = await this.di.getOrCreateDataService(PointOfService).get(pos.id);
      return fullPos?.i18nLanguage?.key;
    } catch (error) {
      console.warn('Impossible de charger la langue par défaut du site :', error);
      return undefined;
    }
  }

  navigateToCheckin() {
    // const route = `/ipad/${this.variables.clientId}/${this.variables.posId}/visitor-type`;
    const route = `/ipad/visitor-type`;
    this.navService.navigateTo(route);
  }

  navigateToCheckout() {
    // const route = `/ipad/${this.variables.clientId}/${this.variables.posId}/checkout`;
    const route = `/ipad/checkout`;
    this.navService.navigateTo(route);
  }

  initQrCode() {
    const urlHome = `${this.baseQrCodeUrl}/home`;
    if (urlHome === this.lastGeneratedQrCodeUrl) return;
    const homeContainers = document.querySelectorAll('.qrCodeHomeContainer');
    if (!homeContainers.length) return;
    this.lastGeneratedQrCodeUrl = urlHome;
    homeContainers.forEach(homeContainer => {
      homeContainer.innerHTML = ''; // Vider la div avant d'ajouter le QR
      this.qrCodeService.appendToElement(homeContainer as HTMLElement, urlHome);
    });
  }

  async onAlert() {
    const pos = this.currentTenantService.currentPointOfService();
    if (!pos || this.isAlerting()) return;

    const confirm = await this.alertModalService.showAlert({
      title: this.translationService.instant('vues.ipad.visits.alert'),
      message: this.translationService.instant('vues.visit.list.alert-confirm'),
      yesButtonText: this.translationService.instant('buttons.yes'),
      noButtonText: this.translationService.instant('buttons.no'),
      Skin: this.Skin
    });
    if (confirm !== AlertModalResult.Yes) return;

    this.isAlerting.set(true);
    try {
      // iPad : alerte sur les visites des 24 dernieres heures du site.
      await this.dataService.execute('POST', 'firealert', { posId: pos.id, last24Hours: true });
      this.alertModalService.showToast(
        this.translationService.instant('vues.visit.list.alert-done'),
        ToastType.Success, 5000);
    } catch (err) {
      console.error('Erreur alerte évacuation:', err);
      this.alertModalService.showToast(this.translationService.instant('messages.error'), ToastType.Error, 5000);
    } finally {
      this.isAlerting.set(false);
    }
  }

  onGenerateEmployeeQR() {
    this.navService.navigateTo('/ipad/employee-qrcode');
  }

  onEmployeesOnly() {
    // TODO: Implement employees only functionality
  }
}
